import { CMMD_PRIORITY_ORDER, normalizeCmmdPriority } from "@cmmd-forge/contracts";
import type { CmmdCampaign, CmmdProject, CmmdWorkspace } from "@cmmd-forge/contracts";
import {
  ChevronDown,
  ChevronUp,
  ChevronsDown,
  ChevronsUp,
  Equal,
  type LucideIcon,
} from "lucide-react";
import type { DataViewGroupBy } from "~/views/viewDefinition";
import { projectDisplayName } from "./projectDisplayName";

function normalizeOptionalId(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim();
  return normalized.length > 0 ? normalized : null;
}

function normalizeRequiredId(value: unknown): string {
  return normalizeOptionalId(value) ?? "";
}

export function normalizeProjectsCampaign(campaign: CmmdCampaign): CmmdCampaign {
  return {
    ...campaign,
    id: normalizeRequiredId(campaign.id),
    workspaceId: normalizeOptionalId(campaign.workspaceId),
  };
}

export function normalizeProjectsProject(project: CmmdProject): CmmdProject {
  return {
    ...project,
    id: normalizeRequiredId(project.id),
    campaignId: normalizeOptionalId(project.campaignId),
    workspaceId: normalizeOptionalId(project.workspaceId),
    ownerId: normalizeOptionalId(project.ownerId),
    teamId: normalizeOptionalId(project.teamId),
  };
}

export function normalizeProjectsWorkspace(workspace: CmmdWorkspace): CmmdWorkspace {
  return {
    ...workspace,
    id: normalizeRequiredId(workspace.id),
  };
}

function workspaceIdentityKey(workspace: Pick<CmmdWorkspace, "name">): string {
  return workspace.name.trim().toLowerCase();
}

/** Copy over the org logo/icon from the membership record when the
 *  project-derived workspace is missing one. */
function enrichWorkspaceIdentity(
  workspace: CmmdWorkspace,
  membershipWorkspaces: ReadonlyArray<CmmdWorkspace>,
): CmmdWorkspace {
  if (workspace.logoUrl || workspace.iconUrl) return workspace;
  const match =
    membershipWorkspaces.find((candidate) => candidate.id === workspace.id) ??
    membershipWorkspaces.find(
      (candidate) => workspaceIdentityKey(candidate) === workspaceIdentityKey(workspace),
    );
  if (!match) return workspace;
  return {
    ...workspace,
    logoUrl: match.logoUrl ?? null,
    iconUrl: match.iconUrl ?? null,
  };
}

/**
 * Build the list of workspaces a user can target when creating or moving a
 * project. The canonical source of truth for "which workspaces can I put a
 * project in" is the user's CMMD workspace membership (`membershipWorkspaces`,
 * from the forge-context bootstrap), NOT the set of workspaces that happen to
 * already contain a project. Deriving the picker from existing projects alone
 * silently drops any workspace without projects yet — most notably the user's
 * personal workspace — so it never appears in the Create project dialog.
 *
 * Project-derived workspaces are kept (and enriched with membership icons)
 * because they cover cross-workspace projects whose workspace may fall outside
 * the membership list; membership-only workspaces are appended so empty
 * workspaces (the personal workspace) become selectable.
 */
export function mergeSelectableWorkspaces(
  projectWorkspaces: ReadonlyArray<CmmdWorkspace>,
  membershipWorkspaces: ReadonlyArray<CmmdWorkspace>,
): ReadonlyArray<CmmdWorkspace> {
  const normalizedMembership = membershipWorkspaces.map(normalizeProjectsWorkspace);
  const enriched = projectWorkspaces.map((workspace) =>
    enrichWorkspaceIdentity(workspace, normalizedMembership),
  );
  const seenById = new Set(enriched.map((workspace) => workspace.id));
  const seenByName = new Set(enriched.map((workspace) => workspaceIdentityKey(workspace)));
  const result = [...enriched];
  for (const workspace of normalizedMembership) {
    if (seenById.has(workspace.id) || seenByName.has(workspaceIdentityKey(workspace))) continue;
    seenById.add(workspace.id);
    seenByName.add(workspaceIdentityKey(workspace));
    result.push(workspace);
  }
  return result;
}

// ── Project status columns (the CMMD project status vocabulary) ────────────────

interface StatusColumnSpec {
  readonly status: string;
  readonly label: string;
  /** Core columns always render; optional ones only when populated. */
  readonly alwaysShow: boolean;
}

const STATUS_COLUMN_ORDER: ReadonlyArray<StatusColumnSpec> = [
  { status: "not_started", label: "Not started", alwaysShow: true },
  { status: "draft", label: "Draft", alwaysShow: false },
  { status: "active", label: "Active", alwaysShow: true },
  { status: "in_progress", label: "In progress", alwaysShow: false },
  { status: "at_risk", label: "At risk", alwaysShow: false },
  { status: "on_hold", label: "On hold", alwaysShow: false },
  { status: "completed", label: "Completed", alwaysShow: true },
  { status: "archived", label: "Archived", alwaysShow: false },
];

const KNOWN_STATUSES = new Set(STATUS_COLUMN_ORDER.map((c) => c.status));

/**
 * The whole status vocabulary, in canonical order.
 *
 * `getStatusOptions` narrows to the statuses a given set of projects actually
 * uses, which is right for a board filter and wrong for an editor: a project
 * can only be moved to a status the picker offers.
 */
export const PROJECT_STATUS_OPTIONS: ReadonlyArray<{ value: string; label: string }> =
  STATUS_COLUMN_ORDER.map((column) => ({ value: column.status, label: column.label }));

/** Canonical, ordered label for a project status (falls back to a humanized form). */
export function projectStatusLabel(status: string | null): string {
  if (!status) return "No status";
  const known = STATUS_COLUMN_ORDER.find((c) => c.status === status);
  if (known) return known.label;
  return status.replaceAll("_", " ").replace(/^\w/, (m) => m.toUpperCase());
}

export type SemanticTone = "neutral" | "info" | "success" | "warning" | "danger" | "muted";

export function projectStatusTone(status: string | null): SemanticTone {
  switch (status) {
    case "active":
    case "in_progress":
      return "info";
    case "completed":
      return "success";
    case "at_risk":
    case "on_hold":
      return "warning";
    case "archived":
    case "cancelled":
      return "muted";
    case null:
    case "not_started":
    case "draft":
    default:
      return "neutral";
  }
}

export function projectPriorityTone(priority: string | null | undefined): SemanticTone {
  switch (priority?.toLowerCase()) {
    case "critical":
    case "urgent":
      return "danger";
    case "high":
      return "warning";
    case "low":
      return "muted";
    case "medium":
    default:
      return "neutral";
  }
}

export function projectPriorityLabel(priority: string | null | undefined): string {
  if (!priority) return "";
  return priority
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

/**
 * Jira-style priority icon descriptor. The SHAPE conveys urgency (chevrons up
 * for high, down for low, equals for medium), and it is the only thing that
 * does for four of the five levels. `null` for none/unknown so callers can
 * render nothing.
 *
 * The ramp used to be red / orange / amber / sky / slate. Two problems. Forge's
 * `--primary` is warm orange, so an orange High chevron read as the brand accent
 * rather than as a priority — and on a real board High is the *mode*, not the
 * exception, so it painted a column of brand-colored arrows down the edge of
 * nearly every card while saying nothing. Color now marks only the level that is
 * genuinely exceptional; the rest separate by shape alone.
 *
 * High was tried at full `--foreground` first and demoted after looking at a real
 * board: it is the modal priority there, so it just traded 18 orange chevrons for
 * 18 white ones at the same weight as the card titles. Critical is the scan target;
 * everything else is detail you read once you are on the card.
 */
export interface ProjectPriorityIcon {
  readonly Icon: LucideIcon;
  /** Tailwind text color class for the icon. */
  readonly colorClass: string;
  /** Accessible label, e.g. "High". */
  readonly label: string;
}

export function projectPriorityIcon(
  priority: string | null | undefined,
): ProjectPriorityIcon | null {
  // Route through the canonical normalizer so `urgent` resolves to `critical`
  // and any unknown value collapses to null (no icon).
  switch (normalizeCmmdPriority(priority)) {
    case "critical":
      return { Icon: ChevronsUp, colorClass: "text-destructive-foreground", label: "Critical" };
    case "high":
      return { Icon: ChevronUp, colorClass: "text-muted-foreground", label: "High" };
    case "medium":
      return { Icon: Equal, colorClass: "text-muted-foreground", label: "Medium" };
    case "low":
      return { Icon: ChevronDown, colorClass: "text-muted-foreground", label: "Low" };
    case "lowest":
      return { Icon: ChevronsDown, colorClass: "text-muted-foreground", label: "Lowest" };
    default:
      return null;
  }
}

export function semanticToneClasses(tone: SemanticTone): string {
  switch (tone) {
    case "info":
      return "border-info-border bg-info-surface text-info-foreground";
    case "success":
      return "border-success-border bg-success-surface text-success-foreground";
    case "warning":
      return "border-warning-border bg-warning-surface text-warning-foreground";
    case "danger":
      return "border-destructive-border bg-destructive-surface text-destructive-foreground";
    case "muted":
      return "border-border bg-muted text-muted-foreground";
    case "neutral":
    default:
      return "border-border bg-muted text-foreground/80";
  }
}

export function semanticDotClasses(tone: SemanticTone): string {
  switch (tone) {
    case "info":
      return "bg-info";
    case "success":
      return "bg-success";
    case "warning":
      return "bg-warning";
    case "danger":
      return "bg-destructive";
    case "muted":
      return "bg-muted-foreground/45";
    case "neutral":
    default:
      return "bg-muted-foreground/55";
  }
}

export interface StatusColumn {
  readonly status: string;
  readonly label: string;
  readonly projects: ReadonlyArray<CmmdProject>;
}

export function getStatusOptions(
  projects: ReadonlyArray<CmmdProject>,
): ReadonlyArray<{ value: string; label: string }> {
  const values = new Set(projects.map((project) => project.status ?? "not_started"));
  return [
    ...STATUS_COLUMN_ORDER.filter((column) => column.alwaysShow || values.has(column.status)).map(
      (column) => ({
        value: column.status,
        label: column.label,
      }),
    ),
    ...[...values]
      .filter((status) => !KNOWN_STATUSES.has(status))
      .toSorted((a, b) => projectStatusLabel(a).localeCompare(projectStatusLabel(b)))
      .map((status) => ({ value: status, label: projectStatusLabel(status) })),
  ];
}

/**
 * Group projects into ordered status columns. Unknown statuses collapse into an
 * "Other" column at the end so nothing is dropped.
 */
export function groupProjectsByStatus(
  projects: ReadonlyArray<CmmdProject>,
): ReadonlyArray<StatusColumn> {
  const byStatus = new Map<string, CmmdProject[]>();
  let hasOther = false;
  for (const project of projects) {
    const raw = project.status ?? "not_started";
    const status = KNOWN_STATUSES.has(raw) ? raw : "__other__";
    if (status === "__other__") hasOther = true;
    const bucket = byStatus.get(status) ?? [];
    bucket.push(project);
    byStatus.set(status, bucket);
  }

  const columns: StatusColumn[] = STATUS_COLUMN_ORDER.filter(
    (c) => c.alwaysShow || (byStatus.get(c.status)?.length ?? 0) > 0,
  ).map((c) => ({ status: c.status, label: c.label, projects: byStatus.get(c.status) ?? [] }));

  if (hasOther) {
    columns.push({
      status: "__other__",
      label: "Other",
      projects: byStatus.get("__other__") ?? [],
    });
  }
  return columns;
}

// ── Campaign swimlanes ─────────────────────────────────────────────────────────

export type ProjectSwimlaneKind = "campaign" | "workspace";

export interface ProjectSwimlane {
  readonly kind: ProjectSwimlaneKind;
  /** Lane id, or null for the catch-all lane. */
  readonly id: string | null;
  readonly campaignId: string | null;
  readonly workspaceId: string | null;
  readonly title: string;
  readonly campaign: CmmdCampaign | null;
  readonly workspace: CmmdWorkspace | null;
  readonly projects: ReadonlyArray<CmmdProject>;
}

/**
 * Group projects into campaign swimlanes, in campaign order, with a trailing
 * "No campaign" lane for unassigned projects. Empty campaigns are still shown so
 * the board reflects the full campaign structure.
 */
export function buildCampaignSwimlanes(
  projects: ReadonlyArray<CmmdProject>,
  campaigns: ReadonlyArray<CmmdCampaign>,
): ReadonlyArray<ProjectSwimlane> {
  const campaignIds = new Set(campaigns.map((campaign) => normalizeRequiredId(campaign.id)));
  const byCampaign = new Map<string, CmmdProject[]>();
  const noCampaign: CmmdProject[] = [];
  for (const project of projects) {
    const campaignId = normalizeOptionalId(project.campaignId);
    if (campaignId && campaignIds.has(campaignId)) {
      const bucket = byCampaign.get(campaignId) ?? [];
      bucket.push(project);
      byCampaign.set(campaignId, bucket);
    } else {
      noCampaign.push(project);
    }
  }

  const lanes: ProjectSwimlane[] = campaigns.map((campaign) => {
    const campaignId = normalizeRequiredId(campaign.id);
    return {
      kind: "campaign",
      id: campaignId,
      campaignId,
      workspaceId: normalizeOptionalId(campaign.workspaceId),
      title: campaign.title,
      campaign,
      workspace: null,
      projects: byCampaign.get(campaignId) ?? [],
    };
  });

  if (noCampaign.length > 0) {
    lanes.push({
      kind: "campaign",
      id: null,
      campaignId: null,
      workspaceId: null,
      title: "No campaign",
      campaign: null,
      workspace: null,
      projects: noCampaign,
    });
  }
  return lanes;
}

export function hasHealthyCampaignLinks(
  projects: ReadonlyArray<CmmdProject>,
  campaigns: ReadonlyArray<CmmdCampaign>,
): boolean {
  if (campaigns.length === 0 || projects.length === 0) return false;
  const campaignIds = new Set(campaigns.map((campaign) => normalizeRequiredId(campaign.id)));
  return projects.some((project) => {
    const campaignId = normalizeOptionalId(project.campaignId);
    return campaignId !== null && campaignIds.has(campaignId);
  });
}

function buildWorkspaceSwimlanes(
  projects: ReadonlyArray<CmmdProject>,
  workspaces: ReadonlyArray<CmmdWorkspace> = [],
  options: { readonly includeEmptyWorkspaces?: boolean } = {},
): ReadonlyArray<ProjectSwimlane> {
  const workspaceById = new Map(workspaces.map((workspace) => [workspace.id, workspace]));
  const byWorkspace = new Map<string, CmmdProject[]>();
  const noWorkspace: CmmdProject[] = [];

  for (const project of projects) {
    if (project.workspaceId) {
      const bucket = byWorkspace.get(project.workspaceId) ?? [];
      bucket.push(project);
      byWorkspace.set(project.workspaceId, bucket);
    } else {
      noWorkspace.push(project);
    }
  }

  const workspaceIds = [
    ...new Set([
      ...workspaces.map((workspace) => workspace.id),
      ...projects.flatMap((project) => (project.workspaceId ? [project.workspaceId] : [])),
    ]),
  ];

  const lanes: ProjectSwimlane[] = workspaceIds.map((workspaceId) => {
    const workspace = workspaceById.get(workspaceId) ?? null;
    const laneProjects = byWorkspace.get(workspaceId) ?? [];
    return {
      kind: "workspace",
      id: workspaceId,
      campaignId: null,
      workspaceId,
      title: workspace?.name ?? laneProjects[0]?.workspaceName ?? `Project ${workspaceId}`,
      campaign: null,
      workspace,
      projects: laneProjects,
    };
  });

  if (noWorkspace.length > 0) {
    lanes.push({
      kind: "workspace",
      id: null,
      campaignId: null,
      workspaceId: null,
      title: "No project",
      campaign: null,
      workspace: null,
      projects: noWorkspace,
    });
  }

  return lanes.filter(
    (lane) => lane.projects.length > 0 || (options.includeEmptyWorkspaces && lane.workspace),
  );
}

export function buildDefaultSwimlanes(
  projects: ReadonlyArray<CmmdProject>,
  campaigns: ReadonlyArray<CmmdCampaign>,
  workspaces: ReadonlyArray<CmmdWorkspace> = [],
): ReadonlyArray<ProjectSwimlane> {
  return hasHealthyCampaignLinks(projects, campaigns)
    ? buildCampaignSwimlanes(projects, campaigns)
    : buildWorkspaceSwimlanes(projects, workspaces);
}

// ── Filter + sort (List view) ──────────────────────────────────────────────────

export interface ProjectFilter {
  readonly search?: string;
  readonly status?: string | null;
  readonly campaignId?: string | null;
  readonly workspaceId?: string | null;
  readonly ownerName?: string | null;
  readonly teamName?: string | null;
  readonly hideArchived?: boolean;
}

export function filterProjects(
  projects: ReadonlyArray<CmmdProject>,
  filter: ProjectFilter,
): ReadonlyArray<CmmdProject> {
  const search = filter.search?.trim().toLowerCase();
  return projects.filter((project) => {
    if (filter.hideArchived && project.status === "archived") return false;
    if (
      search &&
      ![
        // Both names: renaming in Forge must not make a project unfindable by
        // the name the board still uses.
        project.title,
        project.shortName ?? null,
        project.ownerName,
        project.workspaceName,
        project.teamName,
        project.description,
        project.brief,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(search)
    ) {
      return false;
    }
    if (filter.status && (project.status ?? "not_started") !== filter.status) return false;
    if (filter.campaignId !== undefined && filter.campaignId !== null) {
      if (normalizeOptionalId(project.campaignId) !== normalizeOptionalId(filter.campaignId)) {
        return false;
      }
    }
    if (filter.workspaceId !== undefined && filter.workspaceId !== null) {
      if (project.workspaceId !== filter.workspaceId) return false;
    }
    if (filter.ownerName !== undefined && filter.ownerName !== null) {
      if ((project.ownerName ?? "") !== filter.ownerName) return false;
    }
    if (filter.teamName !== undefined && filter.teamName !== null) {
      if ((project.teamName ?? "") !== filter.teamName) return false;
    }
    return true;
  });
}

export type ProjectSortKey =
  | "title"
  | "status"
  | "priority"
  | "campaign"
  | "workspace"
  | "owner"
  | "updated";
export type SortDirection = "asc" | "desc";

/** Rank for priority sorting: canonical order index, unknown/none sorts last. */
function projectPriorityRank(project: CmmdProject): number {
  const normalized = normalizeCmmdPriority(project.priority);
  const index = normalized ? CMMD_PRIORITY_ORDER.indexOf(normalized) : -1;
  return index === -1 ? CMMD_PRIORITY_ORDER.length : index;
}

/**
 * Sort projects by a column. `campaign` sorts by resolved campaign title via the
 * provided lookup so the table sorts on what the user sees, not raw ids.
 * `priority` sorts by canonical severity (critical first) with no/unknown last.
 */
export function sortProjects(
  projects: ReadonlyArray<CmmdProject>,
  key: ProjectSortKey,
  direction: SortDirection,
  campaignTitleById: ReadonlyMap<string, string> = new Map(),
): ReadonlyArray<CmmdProject> {
  const factor = direction === "asc" ? 1 : -1;
  if (key === "priority") {
    return projects.toSorted((a, b) => factor * (projectPriorityRank(a) - projectPriorityRank(b)));
  }
  const valueOf = (project: CmmdProject): string => {
    if (key === "title") return projectDisplayName(project) ?? project.title;
    if (key === "status") return projectStatusLabel(project.status);
    if (key === "campaign") {
      const campaignId = normalizeOptionalId(project.campaignId);
      return campaignId ? (campaignTitleById.get(campaignId) ?? "") : "";
    }
    if (key === "workspace") return project.workspaceName ?? "";
    if (key === "owner") return project.ownerName ?? "";
    return project.updatedAt ?? project.createdAt ?? "";
  };
  return projects.toSorted((a, b) => factor * valueOf(a).localeCompare(valueOf(b)));
}

// ── Grouping (list view) ───────────────────────────────────────────────────────

export interface ProjectGroup {
  /** Stable key for React + collapse state. */
  readonly key: string;
  /** Display label for the group header. */
  readonly label: string;
  readonly items: ReadonlyArray<CmmdProject>;
}

/** A grouping bucket for one project: stable key, header label, sort order. */
export interface ProjectGroupDescriptor {
  readonly key: string;
  readonly label: string;
  readonly order: number;
}

/**
 * The group a single project belongs to under `groupBy` — status column order,
 * priority rank, else alphabetical, with the "no X" bucket last. Shared by the
 * list's `groupProjects` and the DataTable `groupOf` so both agree. `"none"`
 * returns the single flat bucket.
 */
export function projectGroupDescriptor(
  project: CmmdProject,
  groupBy: DataViewGroupBy,
  campaignTitleById: ReadonlyMap<string, string> = new Map(),
): ProjectGroupDescriptor {
  if (groupBy === "status") {
    const raw = project.status ?? "not_started";
    const column = STATUS_COLUMN_ORDER.find((c) => c.status === raw);
    return column
      ? { key: column.status, label: column.label, order: STATUS_COLUMN_ORDER.indexOf(column) }
      : { key: "__other__", label: "Other", order: STATUS_COLUMN_ORDER.length };
  }
  if (groupBy === "priority") {
    const normalized = normalizeCmmdPriority(project.priority);
    return normalized
      ? {
          key: normalized,
          label: projectPriorityLabel(normalized),
          order: CMMD_PRIORITY_ORDER.indexOf(normalized),
        }
      : { key: "__none__", label: "No priority", order: CMMD_PRIORITY_ORDER.length + 1 };
  }
  if (groupBy === "campaign") {
    const campaignId = normalizeOptionalId(project.campaignId);
    return campaignId
      ? { key: campaignId, label: campaignTitleById.get(campaignId) ?? "Campaign", order: 0 }
      : { key: "__none__", label: "No campaign", order: 1 };
  }
  if (groupBy === "owner") {
    const owner = (project.ownerName ?? "").trim();
    return owner
      ? { key: `owner:${owner}`, label: owner, order: 0 }
      : { key: "__none__", label: "Unassigned", order: 1 };
  }
  return { key: "__all__", label: "", order: 0 };
}

/**
 * Group an already-sorted, already-filtered project list for the list view.
 * Empty groups are dropped; within-group order is preserved from the input.
 * `groupBy: "none"` returns a single unlabeled group (flat list).
 */
export function groupProjects(
  projects: ReadonlyArray<CmmdProject>,
  groupBy: DataViewGroupBy,
  campaignTitleById: ReadonlyMap<string, string> = new Map(),
): ReadonlyArray<ProjectGroup> {
  if (groupBy === "none") {
    return [{ key: "__all__", label: "", items: projects }];
  }
  const buckets = new Map<string, { label: string; order: number; items: CmmdProject[] }>();
  for (const project of projects) {
    const d = projectGroupDescriptor(project, groupBy, campaignTitleById);
    const bucket = buckets.get(d.key) ?? { label: d.label, order: d.order, items: [] };
    bucket.items.push(project);
    buckets.set(d.key, bucket);
  }
  return [...buckets.entries()]
    .toSorted(([, a], [, b]) => a.order - b.order || a.label.localeCompare(b.label))
    .map(([key, bucket]) => ({ key, label: bucket.label, items: bucket.items }));
}

export function getUniqueProjectValues(
  projects: ReadonlyArray<CmmdProject>,
  key: "workspaceName" | "ownerName" | "teamName",
): ReadonlyArray<string> {
  return [...new Set(projects.map((project) => project[key]).filter(Boolean) as string[])].toSorted(
    (a, b) => a.localeCompare(b),
  );
}

export function formatProjectDate(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(date);
}
